import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { syncOnLogin } from '~/reducers/common-actions';
import type { RootState } from '~/store';
import { isValidSortMapMode, isValidSortDirection } from './utils';
import type {
    RoutesDrawerSortState,
    RoutesDrawerMapModeResetPayload,
    RoutesDrawerMapModeSortSetting,
    RoutesDrawerMapModeSortDirectionPayload,
    RoutesDrawerMapModeSortMetricPayload
} from './types';
import {
    defaultRoutesDrawerSortDirection,
    defaultRoutesDrawerSort
} from './types';

export const routesDrawerSortSlice = createSlice({
    name: 'routesDrawerSort',
    initialState: defaultRoutesDrawerSort,
    reducers: {
        setMapModeSortMetric: (
            state: RoutesDrawerSortState,
            action: PayloadAction<RoutesDrawerMapModeSortMetricPayload>
        ) => {
            const { mapMode, sortMetric } = action.payload;
            const isValidMapMode = isValidSortMapMode(mapMode);
            if (!isValidMapMode) {
                return state;
            }
            const updatedMapModeSetting: RoutesDrawerMapModeSortSetting = {
                ...state[mapMode],
                sortMetric
            };
            return {
                ...state,
                [mapMode]: updatedMapModeSetting
            };
        },
        setMapModeSortDirection: (
            state: RoutesDrawerSortState,
            action: PayloadAction<RoutesDrawerMapModeSortDirectionPayload>
        ) => {
            const { mapMode, sortDirection } = action.payload;
            const direction = sortDirection.toLowerCase();
            const isValidMapMode = isValidSortMapMode(mapMode);
            const isValidDirection = isValidSortDirection(direction);
            if (!isValidMapMode || !isValidDirection) {
                return state;
            }
            const updatedMapModeSetting: RoutesDrawerMapModeSortSetting = {
                ...state[mapMode],
                sortDirection: direction
            };
            return {
                ...state,
                [mapMode]: updatedMapModeSetting
            };
        },
        resetMapModeSort: (
            state: RoutesDrawerSortState,
            action: PayloadAction<RoutesDrawerMapModeResetPayload>
        ) => {
            const { mapMode } = action.payload;
            const isValidMapMode = isValidSortMapMode(mapMode);
            if (!isValidMapMode) {
                return state;
            }
            return {
                ...state,
                [mapMode]: defaultRoutesDrawerSort[mapMode]
            };
        },
        resetRoutesDrawerSort: (): RoutesDrawerSortState => {
            return defaultRoutesDrawerSort;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(syncOnLogin, (state) => {
            // merge to initial state to ensure new keys for this slice
            // are synced with the current state
            return {
                ...defaultRoutesDrawerSort,
                ...state
            };
        });
    }
});

export const {
    setMapModeSortMetric,
    setMapModeSortDirection,
    resetMapModeSort,
    resetRoutesDrawerSort
} = routesDrawerSortSlice.actions;

export const selectMapModeSortMetric =
    (mapMode: string): ((state: RootState) => string | null) =>
    (state: RootState) => {
        const isValidMapMode = isValidSortMapMode(mapMode);
        if (!isValidMapMode) return null;
        return state.routesDrawerSort[mapMode].sortMetric;
    };

export const selectMapModeSortDirection =
    (mapMode: string): ((state: RootState) => string) =>
    (state: RootState) => {
        const isValidMapMode = isValidSortMapMode(mapMode);
        if (!isValidMapMode) return defaultRoutesDrawerSortDirection;
        return state.routesDrawerSort[mapMode].sortDirection;
    };

export const selectMapModeIsSorted =
    (mapMode: string): ((state: RootState) => boolean) =>
    (state: RootState) => {
        const isValidMapMode = isValidSortMapMode(mapMode);
        return (
            isValidMapMode &&
            Boolean(state.routesDrawerSort[mapMode].sortMetric)
        );
    };

export default routesDrawerSortSlice.reducer;
